import React, { useCallback, useMemo, useState } from "react";
import { useGet, useGetLazy, usePost } from "../../api";
import {
  IAdvertisersResponse,
  IAffiliateAdvertiser,
  INetworkCategories,
  IKindredCouponsResponse,
  IKindredOffersResponse,
  ISyncConfiguration,
} from "../../interfaces";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Offcanvas,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import {
  ExternalLink,
  Search,
  ShoppingBag,
  Tag,
  Target,
  Layers,
  Copy,
  Check,
  Settings,
  AlertTriangle,
  PlusCircle,
} from "react-feather";
import { Link } from "react-router";
import { settingsType, useSettings } from "../../hooks";

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 ms-1"
      style={{ lineHeight: 1, verticalAlign: "middle" }}
      onClick={handleCopy}
      title={copied ? "Copied!" : title || "Copy"}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

interface IFingerprintProductSummary {
  id: string;
  name: string;
  affiliateNetwork: string;
  advertiserId: string;
  merchantName?: string;
  domain?: string;
  price?: number;
  currency?: string;
  inStock?: boolean;
  productUrl?: string;
  categories?: string[];
  canonicalKey?: string | null;
}

interface IFingerprintSearchResult {
  product: IFingerprintProductSummary;
  matchConfidence: number;
  matchType: string;
  matchedKey: string;
  matchReason: string;
}

interface IFingerprintSearchResponse {
  totalCount: number;
  count: number;
  offset: number;
  limit: number;
  results: IFingerprintSearchResult[];
  elapsedMs?: number;
}

interface IFingerprintSearchRequest {
  productName?: string;
  productUrl?: string;
  categoryPath?: string;
  advertiserName?: string;
  domain?: string;
  ean?: string;
  gtin?: string;
  upc?: string;
  sku?: string;
  mpn?: string;
  merchantProductId?: string;
  minimumConfidence?: number;
  offset?: number;
  limit?: number;
}

interface IBasketSearchItem {
  productName?: string;
  productUrl?: string;
  categoryPath?: string;
  advertiserName?: string;
  ean?: string;
  gtin?: string;
  upc?: string;
  sku?: string;
  mpn?: string;
  merchantProductId?: string;
}

interface IBasketSearchRequest {
  merchantDomain: string;
  merchantUrl?: string;
  items: IBasketSearchItem[];
  options?: {
    maxResultsPerItem?: number;
    minimumConfidence?: number;
    maxCandidatesPerItem?: number;
    maxConcurrency?: number;
  };
}

interface IBasketSearchResponse {
  merchantDomain: string;
  items: Array<{
    item: IBasketSearchItem;
    matches: IFingerprintSearchResult[];
    totalCandidatesScored: number;
  }>;
  totalMatchCount: number;
  elapsedMilliseconds?: number;
}

interface ICategoryBrowseProduct {
  id: string;
  name: string;
  affiliateNetwork: string;
  advertiserId: string;
  advertiserName?: string;
  domain?: string;
  price?: number;
  currency?: string;
  inStock?: boolean;
  productUrl?: string;
  matchType?: string;
  categories?: string[];
}

interface ICategoryBrowseResponse {
  categoryPath: string;
  normalizedCategoryPath: string;
  totalCount: number;
  count: number;
  offset: number;
  limit: number;
  products: ICategoryBrowseProduct[];
}

interface ICatalogStatus {
  checked: boolean;
  loading: boolean;
  advertisers: IAffiliateAdvertiser[];
  syncConfigs: Record<string, ISyncConfiguration | null>; // key: `${network}-${advertiserId}`, null = not configured
}

export const MerchantReview: React.FC = () => {
  const [searchMode, setSearchMode] = useState("domain");

  // Domain review state (existing behavior)
  const [domain, setDomain] = useSettings(settingsType.AP_MERCHANT_DOMAIN);
  const [selectedAdv, setSelectedAdv] = useState<IAffiliateAdvertiser | null>(
    null
  );
  const [showPanel, setShowPanel] = useState(false);

  const domainSearchUrl =
    domain && domain.length >= 3
      ? `affiliate-products/advertisers?domain=${encodeURIComponent(domain)}`
      : null;
  const domainSearchResults = useGet<IAdvertisersResponse>(
    domainSearchUrl,
    false
  );

  const categoriesUrl = selectedAdv
    ? `affiliate-products/categories?network=${selectedAdv.affiliateNetwork}&advertiserId=${selectedAdv.advertiserId}`
    : null;
  const categories = useGet<INetworkCategories>(categoriesUrl, false);

  const cleanDomain = domain
    ? domain.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : "";

  const couponsUrl =
    cleanDomain.length >= 3
      ? `kindred/coupons?domain=${encodeURIComponent(cleanDomain)}&limit=50`
      : null;
  const coupons = useGet<IKindredCouponsResponse>(couponsUrl, false);

  const offersUrl =
    cleanDomain.length >= 3
      ? `kindred/offers?domain=${encodeURIComponent(cleanDomain)}&limit=50`
      : null;
  const offers = useGet<IKindredOffersResponse>(offersUrl, false);

  // Fingerprint search mode
  const [fingerprintForm, setFingerprintForm] =
    useState<IFingerprintSearchRequest>({
      productName: "",
      productUrl: "",
      categoryPath: "",
      advertiserName: "",
      domain: "",
      ean: "",
      gtin: "",
      upc: "",
      sku: "",
      mpn: "",
      merchantProductId: "",
      minimumConfidence: 0.3,
      offset: 0,
      limit: 50,
    });
  const fingerprintSearch = usePost<
    IFingerprintSearchResponse,
    IFingerprintSearchRequest
  >();
  const basketSearch = usePost<IBasketSearchResponse, IBasketSearchRequest>();

  const [basketDomain, setBasketDomain] = useState("");
  const [basketUrl, setBasketUrl] = useState("");
  const [basketItemsText, setBasketItemsText] = useState(
    '{\n  "productName": "Philips PerfectCare 8000",\n  "sku": "PSG8300/80"\n}\n{\n  "productName": "Sony WH-1000XM5",\n  "categoryPath": "Electronics > Audio > Headphones"\n}'
  );
  const [basketMinConfidence, setBasketMinConfidence] = useState("0.5");
  const [basketMaxResultsPerItem, setBasketMaxResultsPerItem] = useState("5");
  const [basketParseError, setBasketParseError] = useState<string | null>(null);

  // Catalog diagnostics (shown when fingerprint search returns 0 results)
  const [catalogStatus, setCatalogStatus] = useState<ICatalogStatus | null>(
    null
  );
  const [configureLoading, setConfigureLoading] = useState<Set<string>>(
    new Set()
  );
  const [configureSuccess, setConfigureSuccess] = useState<Set<string>>(
    new Set()
  );

  const catalogAdvGet = useGetLazy<IAdvertisersResponse>();
  const syncConfigGet = useGetLazy<ISyncConfiguration>();
  const createSyncConfigPost = usePost<ISyncConfiguration, void>();
  const enableProductsPost = usePost<void, void>();

  // Category browse mode
  const [categoryPath, setCategoryPath] = useState("");
  const [categoryDomain, setCategoryDomain] = useState("");
  const [categoryOffset, setCategoryOffset] = useState(0);
  const [categoryLimit, setCategoryLimit] = useState(50);
  const [submittedCategoryKey, setSubmittedCategoryKey] = useState("");

  const categoryBrowseUrl = useMemo(() => {
    if (!submittedCategoryKey || !categoryPath.trim()) return null;
    const params = new URLSearchParams();
    params.set("categoryPath", categoryPath.trim());
    if (categoryDomain.trim()) params.set("domain", categoryDomain.trim());
    params.set("offset", String(categoryOffset));
    params.set("limit", String(categoryLimit));
    return `affiliate-products/categories/search?${params.toString()}`;
  }, [
    submittedCategoryKey,
    categoryPath,
    categoryDomain,
    categoryOffset,
    categoryLimit,
  ]);

  const categoryBrowse = useGet<ICategoryBrowseResponse>(
    categoryBrowseUrl,
    false
  );

  const handleDomainSearch = useCallback(async () => {
    if (domain && domain.length >= 3) {
      await Promise.all([
        domainSearchResults.refetch(),
        coupons.refetch(),
        offers.refetch(),
      ]);
    }
  }, [domain, domainSearchResults, coupons, offers]);

  const handleSelectAdvertiser = useCallback(
    async (adv: IAffiliateAdvertiser) => {
      setSelectedAdv(adv);
      setTimeout(() => categories.refetch(), 0);
    },
    [categories]
  );

  const checkCatalog = useCallback(
    async (domain: string) => {
      setCatalogStatus({
        checked: false,
        loading: true,
        advertisers: [],
        syncConfigs: {},
      });
      try {
        const advResponse = await catalogAdvGet.execute(
          `affiliate-products/advertisers?domain=${encodeURIComponent(domain)}&limit=50`
        );
        const advertisers = advResponse.advertisers;

        const entries = await Promise.all(
          advertisers.map(async (adv) => {
            const key = `${adv.affiliateNetwork}-${adv.advertiserId}`;
            try {
              const config = await syncConfigGet.execute(
                `affiliate-products/sync/config/${adv.affiliateNetwork}/${adv.advertiserId}`
              );
              return [key, config] as const;
            } catch {
              return [key, null] as const;
            }
          })
        );

        setCatalogStatus({
          checked: true,
          loading: false,
          advertisers,
          syncConfigs: Object.fromEntries(entries),
        });
      } catch {
        setCatalogStatus({
          checked: true,
          loading: false,
          advertisers: [],
          syncConfigs: {},
        });
      }
    },
    [catalogAdvGet, syncConfigGet]
  );

  const handleConfigure = useCallback(
    async (adv: IAffiliateAdvertiser) => {
      const key = `${adv.affiliateNetwork}-${adv.advertiserId}`;
      setConfigureLoading((prev) => new Set(prev).add(key));
      try {
        await createSyncConfigPost.execute(
          `affiliate-products/sync/config/${adv.affiliateNetwork}/${adv.advertiserId}`
        );
        await enableProductsPost.execute(
          `affiliate-products/sync/config/${adv.affiliateNetwork}/${adv.advertiserId}/enable-products`
        );
        setConfigureSuccess((prev) => new Set(prev).add(key));
        setCatalogStatus((prev) =>
          prev
            ? {
                ...prev,
                syncConfigs: {
                  ...prev.syncConfigs,
                  [key]: {
                    id: "",
                    affiliateNetwork: adv.affiliateNetwork,
                    advertiserId: adv.advertiserId,
                    advertiserName: adv.name,
                    productIngestionEnabled: true,
                    isActive: true,
                  } as ISyncConfiguration,
                },
              }
            : prev
        );
      } finally {
        setConfigureLoading((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [createSyncConfigPost, enableProductsPost]
  );

  const handleFingerprintSearch = useCallback(async () => {
    setCatalogStatus(null);
    setConfigureSuccess(new Set());
    const payload: IFingerprintSearchRequest = {
      productName: fingerprintForm.productName?.trim() || undefined,
      productUrl: fingerprintForm.productUrl?.trim() || undefined,
      categoryPath: fingerprintForm.categoryPath?.trim() || undefined,
      advertiserName: fingerprintForm.advertiserName?.trim() || undefined,
      domain: fingerprintForm.domain?.trim() || undefined,
      ean: fingerprintForm.ean?.trim() || undefined,
      gtin: fingerprintForm.gtin?.trim() || undefined,
      upc: fingerprintForm.upc?.trim() || undefined,
      sku: fingerprintForm.sku?.trim() || undefined,
      mpn: fingerprintForm.mpn?.trim() || undefined,
      merchantProductId: fingerprintForm.merchantProductId?.trim() || undefined,
      minimumConfidence: fingerprintForm.minimumConfidence,
      offset: fingerprintForm.offset,
      limit: fingerprintForm.limit,
    };

    const result = await fingerprintSearch.execute(
      "affiliate-products/search/fingerprint",
      payload
    );
    if (result?.totalCount === 0 && payload.domain) {
      checkCatalog(payload.domain);
    }
  }, [fingerprintForm, fingerprintSearch, checkCatalog]);

  const handleBasketSearch = useCallback(async () => {
    setBasketParseError(null);
    const trimmedDomain = basketDomain.trim();
    if (!trimmedDomain) {
      setBasketParseError("Merchant domain is required.");
      return;
    }

    const lines = basketItemsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    let items: IBasketSearchItem[] = [];

    try {
      if (lines.length === 1 && basketItemsText.trim().startsWith("[")) {
        const parsed = JSON.parse(basketItemsText);
        items = Array.isArray(parsed) ? parsed : [];
      } else {
        items = lines.map((line) => JSON.parse(line));
      }
    } catch {
      setBasketParseError(
        "Invalid items JSON. Use one JSON object per line or a JSON array."
      );
      return;
    }

    if (!items.length) {
      setBasketParseError("At least one basket item is required.");
      return;
    }

    const payload: IBasketSearchRequest = {
      merchantDomain: trimmedDomain,
      merchantUrl: basketUrl.trim() || undefined,
      items,
      options: {
        minimumConfidence: Number(basketMinConfidence),
        maxResultsPerItem: Number(basketMaxResultsPerItem),
      },
    };

    await basketSearch.execute("affiliate-products/search/basket", payload);
  }, [
    basketDomain,
    basketUrl,
    basketItemsText,
    basketMinConfidence,
    basketMaxResultsPerItem,
    basketSearch,
  ]);

  const handleCategoryBrowse = useCallback(async () => {
    if (!categoryPath.trim()) return;
    setSubmittedCategoryKey(`${Date.now()}`);
    setTimeout(() => categoryBrowse.refetch(), 0);
  }, [categoryPath, categoryBrowse]);

  const hasFingerprintCriteria =
    !!fingerprintForm.productName?.trim() ||
    !!fingerprintForm.productUrl?.trim() ||
    !!fingerprintForm.categoryPath?.trim() ||
    !!fingerprintForm.advertiserName?.trim() ||
    !!fingerprintForm.domain?.trim() ||
    !!fingerprintForm.ean?.trim() ||
    !!fingerprintForm.gtin?.trim() ||
    !!fingerprintForm.upc?.trim() ||
    !!fingerprintForm.sku?.trim() ||
    !!fingerprintForm.mpn?.trim() ||
    !!fingerprintForm.merchantProductId?.trim();
  const hasFingerprintDomain = !!fingerprintForm.domain?.trim();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Merchant Review</h4>
        {searchMode === "domain" && (coupons.data || offers.data) && (
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => setShowPanel(true)}
          >
            <Tag size={14} className="me-1" />
            Coupons & Offers (
            {(coupons.data?.results.length || 0) +
              (offers.data?.results.length || 0)}
            )
          </Button>
        )}
      </div>

      <Tabs
        activeKey={searchMode}
        onSelect={(key) => setSearchMode(key || "domain")}
        className="mb-3"
      >
        <Tab eventKey="domain" title="Domain Review">
          <p className="small text-muted mt-3">
            Quick merchant review by domain (product catalog + coupons/offers).
          </p>

          <Row className="mb-3 g-2">
            <Col md={5}>
              <Form.Control
                size="sm"
                placeholder="Enter merchant domain (e.g. skechers.com)..."
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleDomainSearch()}
              />
            </Col>
            <Col md={2}>
              <Button
                size="sm"
                variant="primary"
                onClick={handleDomainSearch}
                disabled={
                  domainSearchResults.loading || !domain || domain.length < 3
                }
              >
                <Search size={14} className="me-1" />
                Review
              </Button>
            </Col>
          </Row>

          {domainSearchResults.loading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Searching...
            </div>
          )}

          {domainSearchResults.data && (
            <Row>
              <Col md={showPanel ? 8 : 12}>
                <Card className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>
                      <ShoppingBag size={16} className="me-2" />
                      Product Catalog ({domainSearchResults.data.count}{" "}
                      advertisers)
                    </span>
                  </Card.Header>
                  <Card.Body>
                    {domainSearchResults.data.advertisers.length === 0 ? (
                      <p className="text-muted mb-0">
                        No product catalog found for this merchant.
                      </p>
                    ) : (
                      <Table
                        striped
                        hover
                        size="sm"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <thead>
                          <tr>
                            <th>Network</th>
                            <th>Name</th>
                            <th>Products</th>
                            <th>Categories</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {domainSearchResults.data.advertisers.map((adv) => (
                            <tr
                              key={adv.id}
                              className={
                                selectedAdv?.id === adv.id ? "table-active" : ""
                              }
                            >
                              <td>
                                <Badge bg="dark">{adv.affiliateNetwork}</Badge>
                              </td>
                              <td>
                                <strong>{adv.name}</strong>
                                <br />
                                <small className="text-muted">
                                  {adv.domain || adv.advertiserId}
                                </small>
                              </td>
                              <td>{adv.productCount.toLocaleString()}</td>
                              <td>
                                {adv.categories.slice(0, 3).map((cat, idx) => (
                                  <Badge
                                    key={idx}
                                    bg="light"
                                    text="dark"
                                    className="me-1"
                                  >
                                    {cat}
                                  </Badge>
                                ))}
                                {adv.categories.length > 3 && (
                                  <Badge bg="light" text="dark">
                                    +{adv.categories.length - 3}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant={
                                    selectedAdv?.id === adv.id
                                      ? "primary"
                                      : "outline-primary"
                                  }
                                  onClick={() => handleSelectAdvertiser(adv)}
                                >
                                  View Categories
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                {selectedAdv && (
                  <Card className="mb-3">
                    <Card.Header>
                      Categories for {selectedAdv.name} (
                      {selectedAdv.affiliateNetwork})
                    </Card.Header>
                    <Card.Body>
                      {categories.loading && (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" /> Loading
                          categories...
                        </div>
                      )}
                      {categories.data && (
                        <Table
                          striped
                          hover
                          size="sm"
                          style={{ fontSize: "0.85rem" }}
                        >
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Products</th>
                              <th>Last Updated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(categories.data.categories || []).map((cat) => (
                              <tr key={cat.category}>
                                <td>{cat.category}</td>
                                <td>{cat.productCount.toLocaleString()}</td>
                                <td>
                                  <small className="text-muted">
                                    {cat.lastUpdatedAt
                                      ? new Date(
                                          cat.lastUpdatedAt
                                        ).toLocaleDateString()
                                      : "—"}
                                  </small>
                                </td>
                              </tr>
                            ))}
                            {(categories.data.categories || []).length ===
                              0 && (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="text-center text-muted"
                                >
                                  No categories found. Run a sync first.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          )}
        </Tab>

        <Tab eventKey="fingerprint" title="Fingerprint Search">
          <div className="mt-3">
            <p className="small text-muted mb-3">
              Search products using identifiers, name, category path, merchant
              domain, and confidence threshold.
            </p>

            <Card className="mb-3">
              <Card.Body>
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Label className="small">Product Name</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.productName}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          productName: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Category Path</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="Home > Appliances > Ironing"
                      value={fingerprintForm.categoryPath}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          categoryPath: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Product URL</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="https://www.currys.co.uk/products/..."
                      value={fingerprintForm.productUrl}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          productUrl: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Domain</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="currys.co.uk"
                      value={fingerprintForm.domain}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          domain: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">Advertiser</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.advertiserName}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          advertiserName: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">SKU</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.sku}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          sku: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">EAN</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.ean}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          ean: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">GTIN</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.gtin}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          gtin: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">UPC</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.upc}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          upc: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">MPN</Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.mpn}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          mpn: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">
                      Merchant Product ID
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={fingerprintForm.merchantProductId}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          merchantProductId: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">Min Confidence</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={1}
                      step={0.1}
                      value={fingerprintForm.minimumConfidence}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          minimumConfidence: Number(e.target.value),
                        }))
                      }
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">Limit</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      max={200}
                      step={1}
                      value={fingerprintForm.limit}
                      onChange={(e) =>
                        setFingerprintForm((prev) => ({
                          ...prev,
                          limit: Number(e.target.value),
                        }))
                      }
                    />
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleFingerprintSearch}
                      disabled={
                        fingerprintSearch.loading ||
                        !hasFingerprintCriteria ||
                        !hasFingerprintDomain
                      }
                    >
                      <Target size={14} className="me-1" /> Search
                    </Button>
                  </Col>
                </Row>
                {!hasFingerprintDomain && (
                  <Alert variant="warning" className="mt-3 mb-0 py-2">
                    Domain is now required for fingerprint search.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {fingerprintSearch.error && (
              <Alert variant="danger">{fingerprintSearch.error.message}</Alert>
            )}

            {fingerprintSearch.loading && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Searching...
              </div>
            )}

            {fingerprintSearch.data && (
              <Card>
                <Card.Header>
                  Fingerprint Matches ({fingerprintSearch.data.count} /{" "}
                  {fingerprintSearch.data.totalCount})
                  {fingerprintSearch.data.elapsedMs != null && (
                    <span className="text-muted ms-2">
                      in {fingerprintSearch.data.elapsedMs}ms
                    </span>
                  )}
                </Card.Header>
                <Card.Body>
                  <Table
                    striped
                    hover
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Domain</th>
                        <th>Categories</th>
                        <th>Confidence</th>
                        <th>Match Type</th>
                        <th>Reason</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fingerprintSearch.data.results.map((match) => (
                        <tr key={`${match.product.id}-${match.matchedKey}`}>
                          <td>
                            <Link
                              to={`/affiliate-products/products?network=${encodeURIComponent(match.product.affiliateNetwork)}&advertiserId=${encodeURIComponent(match.product.advertiserId)}`}
                              className="fw-bold text-decoration-none"
                            >
                              {match.product.name}
                            </Link>
                            <br />
                            <small>
                              <Link
                                to={`/affiliate-products/advertiser/${encodeURIComponent(match.product.affiliateNetwork)}/${encodeURIComponent(match.product.advertiserId)}`}
                                className="text-muted text-decoration-none"
                              >
                                {match.product.merchantName ||
                                  match.product.advertiserId}
                              </Link>
                            </small>
                            {match.product.canonicalKey && (
                              <div style={{ fontSize: "0.75rem" }}>
                                <span className="text-muted font-monospace">
                                  {match.product.canonicalKey}
                                </span>
                                <CopyButton
                                  text={match.product.canonicalKey}
                                  title="Copy Canonical Key"
                                />
                              </div>
                            )}
                          </td>
                          <td>{match.product.domain || "—"}</td>
                          <td>
                            {match.product.categories &&
                            match.product.categories.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {match.product.categories
                                  .slice(0, 2)
                                  .map((cat, idx) => (
                                    <Badge
                                      key={idx}
                                      bg="light"
                                      text="dark"
                                      className="me-1"
                                    >
                                      {cat}
                                    </Badge>
                                  ))}
                                {match.product.categories.length > 2 && (
                                  <Badge bg="light" text="dark">
                                    +{match.product.categories.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            <Badge
                              bg={
                                match.matchConfidence >= 0.7
                                  ? "success"
                                  : "warning"
                              }
                            >
                              {match.matchConfidence.toFixed(2)}
                            </Badge>
                          </td>
                          <td>{match.matchType}</td>
                          <td>
                            <small>{match.matchReason}</small>
                          </td>
                          <td>
                            {match.product.productUrl ? (
                              <a
                                href={match.product.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink size={14} />
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                      {fingerprintSearch.data.results.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            No matching products found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {fingerprintSearch.data?.totalCount === 0 && (
              <Card className="mt-3" style={{ borderColor: "#ffc107" }}>
                <Card.Header
                  className="d-flex align-items-center gap-2"
                  style={{ backgroundColor: "rgba(255,193,7,0.1)" }}
                >
                  <AlertTriangle size={16} className="text-warning" />
                  <span>
                    Catalog diagnostics for{" "}
                    <strong>{fingerprintForm.domain}</strong>
                  </span>
                </Card.Header>
                <Card.Body>
                  {catalogStatus?.loading && (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" /> Checking catalog…
                    </div>
                  )}

                  {catalogStatus?.checked &&
                    catalogStatus.advertisers.length === 0 && (
                      <Alert variant="warning" className="mb-2 py-2">
                        No product catalog exists for{" "}
                        <strong>{fingerprintForm.domain}</strong>. This domain
                        has no advertisers in our system.
                      </Alert>
                    )}

                  {catalogStatus?.checked &&
                    catalogStatus.advertisers.length > 0 && (
                      <>
                        <p className="small text-muted mb-2">
                          {catalogStatus.advertisers.length} advertiser(s) found
                          for this domain — check their sync status below:
                        </p>
                        <Table size="sm" style={{ fontSize: "0.85rem" }}>
                          <thead>
                            <tr>
                              <th>Network</th>
                              <th>Advertiser</th>
                              <th>Catalog Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {catalogStatus.advertisers.map((adv) => {
                              const key = `${adv.affiliateNetwork}-${adv.advertiserId}`;
                              const config = catalogStatus.syncConfigs[key];
                              const isConfigured = config !== null;
                              const isLoading = configureLoading.has(key);
                              const isDone = configureSuccess.has(key);
                              return (
                                <tr key={key}>
                                  <td>
                                    <Badge bg="dark">
                                      {adv.affiliateNetwork}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Link
                                      to={`/affiliate-products/advertiser/${encodeURIComponent(adv.affiliateNetwork)}/${encodeURIComponent(adv.advertiserId)}`}
                                      className="text-decoration-none"
                                    >
                                      {adv.name}
                                    </Link>
                                    {adv.domain && (
                                      <small className="text-muted ms-1">
                                        ({adv.domain})
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    {isDone ? (
                                      <Badge bg="success">Configured</Badge>
                                    ) : isConfigured ? (
                                      config?.productIngestionEnabled ? (
                                        <Badge bg="success">Syncing</Badge>
                                      ) : (
                                        <Badge bg="warning" text="dark">
                                          Categories Only
                                        </Badge>
                                      )
                                    ) : (
                                      <Badge bg="secondary">
                                        Not Configured
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    {!isConfigured && !isDone && (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => handleConfigure(adv)}
                                        disabled={isLoading}
                                      >
                                        {isLoading ? (
                                          <Spinner
                                            animation="border"
                                            size="sm"
                                          />
                                        ) : (
                                          <>
                                            <Settings
                                              size={12}
                                              className="me-1"
                                            />
                                            Configure &amp; Enable Products
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}

                  <div
                    className={
                      catalogStatus?.checked ? "mt-3 pt-3 border-top" : ""
                    }
                  >
                    <Link
                      to="/affiliate-products/manual-entry"
                      className="d-inline-flex align-items-center gap-1 text-decoration-none"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <PlusCircle size={14} />
                      Add this product manually
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        </Tab>

        <Tab eventKey="basket" title="Basket Search">
          <div className="mt-3">
            <p className="small text-muted mb-3">
              Search multiple products in one call using a shared merchant
              domain.
            </p>

            <Card className="mb-3">
              <Card.Body>
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Label className="small">Merchant Domain</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="currys.co.uk"
                      value={basketDomain}
                      onChange={(e) => setBasketDomain(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">
                      Merchant URL (optional)
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="https://currys.co.uk/cart"
                      value={basketUrl}
                      onChange={(e) => setBasketUrl(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">Min Confidence</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={1}
                      step={0.1}
                      value={basketMinConfidence}
                      onChange={(e) => setBasketMinConfidence(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">Max/Item</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      max={50}
                      value={basketMaxResultsPerItem}
                      onChange={(e) =>
                        setBasketMaxResultsPerItem(e.target.value)
                      }
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small">Items JSON</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={basketItemsText}
                      onChange={(e) => setBasketItemsText(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Use one JSON object per line, or a single JSON array.
                    </Form.Text>
                  </Col>
                  <Col md={12}>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleBasketSearch}
                      disabled={basketSearch.loading || !basketDomain.trim()}
                    >
                      <Target size={14} className="me-1" /> Search Basket
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {basketParseError && (
              <Alert variant="warning">{basketParseError}</Alert>
            )}
            {basketSearch.error && (
              <Alert variant="danger">{basketSearch.error.message}</Alert>
            )}

            {basketSearch.data && (
              <Card>
                <Card.Header>
                  Basket Results ({basketSearch.data.totalMatchCount} matches)
                  {basketSearch.data.elapsedMilliseconds != null && (
                    <span className="text-muted ms-2">
                      in {basketSearch.data.elapsedMilliseconds}ms
                    </span>
                  )}
                </Card.Header>
                <Card.Body>
                  {basketSearch.data.items.map((entry, idx) => (
                    <Card key={idx} className="mb-3">
                      <Card.Header>
                        <strong>Item {idx + 1}:</strong>{" "}
                        {entry.item.productName ||
                          entry.item.sku ||
                          "(no name)"}
                      </Card.Header>
                      <Card.Body>
                        {entry.matches.length > 0 ? (
                          <Table
                            striped
                            hover
                            size="sm"
                            style={{ fontSize: "0.85rem" }}
                          >
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Categories</th>
                                <th>Confidence</th>
                                <th>Match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.matches.map((match) => (
                                <tr
                                  key={`${match.product.id}-${match.matchedKey}`}
                                >
                                  <td>
                                    <strong>{match.product.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {match.product.domain || "—"}
                                    </small>
                                  </td>
                                  <td>
                                    {match.product.categories &&
                                    match.product.categories.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {match.product.categories
                                          .slice(0, 2)
                                          .map((cat, cidx) => (
                                            <Badge
                                              key={cidx}
                                              bg="light"
                                              text="dark"
                                              className="me-1"
                                            >
                                              {cat}
                                            </Badge>
                                          ))}
                                        {match.product.categories.length >
                                          2 && (
                                          <Badge bg="light" text="dark">
                                            +
                                            {match.product.categories.length -
                                              2}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        match.matchConfidence >= 0.7
                                          ? "success"
                                          : "warning"
                                      }
                                    >
                                      {match.matchConfidence.toFixed(2)}
                                    </Badge>
                                  </td>
                                  <td>{match.matchType}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted mb-0">
                            No matches found ({entry.totalCandidatesScored}{" "}
                            candidates scored)
                          </p>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            )}
          </div>
        </Tab>

        <Tab eventKey="category" title="Category Browse">
          <div className="mt-3">
            <p className="small text-muted mb-3">
              Browse products by category path with optional domain filter.
            </p>

            <Card className="mb-3">
              <Card.Body>
                <Row className="g-2">
                  <Col md={5}>
                    <Form.Label className="small">Category Path</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="Home > Appliances > Ironing"
                      value={categoryPath}
                      onChange={(e) => setCategoryPath(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleCategoryBrowse()
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">Domain (optional)</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="currys.co.uk"
                      value={categoryDomain}
                      onChange={(e) => setCategoryDomain(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">Limit</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      max={200}
                      value={categoryLimit}
                      onChange={(e) => setCategoryLimit(Number(e.target.value))}
                    />
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleCategoryBrowse}
                      disabled={!categoryPath.trim() || categoryBrowse.loading}
                    >
                      <Layers size={14} className="me-1" /> Browse
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {categoryBrowse.error && (
              <Alert variant="danger">{categoryBrowse.error.message}</Alert>
            )}

            {categoryBrowse.loading && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            )}

            {categoryBrowse.data && (
              <Card>
                <Card.Header>
                  Category Results ({categoryBrowse.data.count} /{" "}
                  {categoryBrowse.data.totalCount})
                </Card.Header>
                <Card.Body>
                  <Table
                    striped
                    hover
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Merchant</th>
                        <th>Categories</th>
                        <th>Price</th>
                        <th>Match</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryBrowse.data.products.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <strong>{product.name}</strong>
                            <br />
                            <small className="text-muted">
                              {product.affiliateNetwork} ·{" "}
                              {product.advertiserId}
                            </small>
                          </td>
                          <td>
                            {product.advertiserName || product.domain || "—"}
                          </td>
                          <td>
                            {product.categories &&
                            product.categories.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {product.categories
                                  .slice(0, 2)
                                  .map((cat, idx) => (
                                    <Badge
                                      key={idx}
                                      bg="light"
                                      text="dark"
                                      className="me-1"
                                    >
                                      {cat}
                                    </Badge>
                                  ))}
                                {product.categories.length > 2 && (
                                  <Badge bg="light" text="dark">
                                    +{product.categories.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            {product.price != null
                              ? `${product.currency || ""} ${product.price.toFixed(2)}`
                              : "—"}
                          </td>
                          <td>
                            <Badge bg="secondary">
                              {product.matchType || "—"}
                            </Badge>
                          </td>
                          <td>
                            {product.productUrl ? (
                              <a
                                href={product.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink size={14} />
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                      {categoryBrowse.data.products.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            No products found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </div>
        </Tab>
      </Tabs>

      <Offcanvas
        show={showPanel}
        onHide={() => setShowPanel(false)}
        placement="end"
        style={{ width: "500px" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Coupons & Offers — {cleanDomain || "—"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Tabs defaultActiveKey="coupons" className="mb-3">
            <Tab
              eventKey="coupons"
              title={`Coupons (${coupons.data?.results.length || 0})`}
            >
              {coupons.loading && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {coupons.data && (
                <ListGroup variant="flush">
                  {coupons.data.results.map((coupon) => (
                    <ListGroup.Item
                      key={coupon.id}
                      className="px-0 py-2"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <Badge bg="primary" className="me-1">
                            {coupon.code}
                          </Badge>
                          <Badge
                            bg={
                              coupon.publishedStatus === 2
                                ? "success"
                                : "secondary"
                            }
                            className="me-1"
                          >
                            {coupon.publishedStatus === 2
                              ? "Approved"
                              : `Status ${coupon.publishedStatus}`}
                          </Badge>
                        </div>
                        <small className="text-muted">
                          {coupon.discount ? `${coupon.discount}%` : ""}
                        </small>
                      </div>
                      {coupon.summary && (
                        <small className="text-muted d-block mt-1">
                          {coupon.summary}
                        </small>
                      )}
                    </ListGroup.Item>
                  ))}
                  {coupons.data.results.length === 0 && (
                    <ListGroup.Item className="text-muted text-center">
                      No coupons found for this domain.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              )}
            </Tab>
            <Tab
              eventKey="offers"
              title={`Offers (${offers.data?.results.length || 0})`}
            >
              {offers.loading && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {offers.data && (
                <ListGroup variant="flush">
                  {offers.data.results.map((offer) => (
                    <ListGroup.Item
                      key={offer.id}
                      className="px-0 py-2"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <Badge bg="light" text="dark" className="me-1">
                            {offer.brandName}
                          </Badge>
                          <Badge
                            bg={
                              offer.publishedStatus === 2
                                ? "success"
                                : "secondary"
                            }
                            className="me-1"
                          >
                            {offer.publishedStatus === 2
                              ? "Approved"
                              : `Status ${offer.publishedStatus}`}
                          </Badge>
                        </div>
                        <small className="text-muted">{offer.source}</small>
                      </div>
                      <div>{offer.summary || "Offer"}</div>
                    </ListGroup.Item>
                  ))}
                  {offers.data.results.length === 0 && (
                    <ListGroup.Item className="text-muted text-center">
                      No offers found for this domain.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              )}
            </Tab>
          </Tabs>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};
